import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ShieldCheckIcon, CalendarDaysIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { PortraitFrame } from '../common/editorial/EditorialControls';

const AdvisorProfile = () => {
  const { advisorId } = useParams();
  const navigate = useNavigate();
  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`/api/community/advisors/${advisorId}`)
      .then((r) => setAdvisor(r.data))
      .catch(() => navigate('/advisors'))
      .finally(() => setLoading(false));
  }, [advisorId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading…</p>
      </div>
    );
  }
  if (!advisor) return null;

  return (
    <div className="min-h-screen bg-bone p-6 md:p-8">
      <div className="max-w-3xl mx-auto">
        <button
          type="button"
          onClick={() => navigate('/advisors')}
          className="flex items-center text-secondary hover:text-primary mb-6 transition-colors duration-300"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" /> Back to advisors
        </button>

        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="aura-card mb-6"
        >
          <div className="flex items-start gap-6 mb-6 pb-6 border-b border-hairline">
            <PortraitFrame
              src={advisor.photo_url}
              alt={advisor.name}
              initials={advisor.name?.[0]?.toUpperCase()}
              size="lg"
            />
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="font-display text-h1 text-primary">{advisor.name}</h1>
                <ShieldCheckIcon className="w-5 h-5 text-pine" title="Verified" />
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {(advisor.credentials || []).map((c) => (
                  <span
                    key={c}
                    className="text-label px-2 py-0.5 rounded-md bg-pine-tint text-pine border border-hairline font-medium"
                  >
                    {c}
                  </span>
                ))}
              </div>
              <p className="text-label text-secondary">
                <span className="figure">{advisor.years_experience}</span> years experience
              </p>
            </div>
          </div>

          <section className="mb-6 pb-6 border-b border-hairline">
            <h2 className="text-label text-secondary mb-2">About</h2>
            <p className="text-body text-primary leading-relaxed">{advisor.bio || 'No bio provided.'}</p>
          </section>

          <section className="mb-6 pb-6 border-b border-hairline">
            <h2 className="text-label text-secondary mb-2">Specialties</h2>
            <div className="flex flex-wrap gap-2">
              {(advisor.specialties || []).map((s) => (
                <span key={s} className="text-label px-3 py-1 rounded-md border border-hairline text-secondary">
                  {s}
                </span>
              ))}
            </div>
          </section>

          <section className="pt-2">
            {advisor.hourly_rate && (
              <div className="mb-4">
                <span className="figure text-display text-primary">${advisor.hourly_rate}</span>
                <span className="text-body text-secondary ml-1">/session</span>
              </div>
            )}
            {advisor.booking_url ? (
              <a
                href={advisor.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full flex items-center justify-center gap-2 py-3"
              >
                <CalendarDaysIcon className="w-5 h-5" />
                Book a session
              </a>
            ) : (
              <p className="text-center text-secondary">Booking link coming soon</p>
            )}
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default AdvisorProfile;
